import React, { useEffect, useRef, useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

// Import Scrollbar
import SimpleBar from "simplebar-react";

// MetisMenu
import MetisMenu from "metismenujs";
import withRouter from "components/Common/withRouter";
import { Link } from "react-router-dom";

// i18n
import { withTranslation } from "react-i18next";

// Import fungsi untuk mendapatkan level pengguna
import { getUserLevel } from '../../helpers/auth';

const SidebarContent = props => {
  const ref = useRef();
  const { pathname } = useLocation();
  const [userLevel, setUserLevel] = useState(null); // State untuk menyimpan level pengguna
  const metisMenuInstanceRef = useRef(null);

    const ACCENT_ORANGE = "#FF8C00";        // Oranye untuk judul menu normal
  const TEXT_HIGHLIGHT_ORANGE = "rgb(13, 38, 75)"; // Oranye terang untuk highlight "Pusat Langganan"
  const TEXT_BLUE = "#1E90FF";             // Biru untuk teks menu utama
  const TEXT_DEFAULT = "#6c757d";          // Warna default (secondary)



  // Effect untuk mendapatkan level pengguna saat komponen dimuat
  useEffect(() => {
    const level = getUserLevel();
    setUserLevel(level);
  }, []);

  const activateParentDropdown = useCallback((item) => {
    if (!item || !item.classList) return;

    item.classList.add("active");

    let currentParent = item.parentElement;
    if (currentParent) {
      const parent2El = currentParent.childNodes[1];
      if (parent2El && parent2El.id !== "side-menu" && parent2El.classList) {
        parent2El.classList.add("mm-show");
      }

      currentParent.classList.add("mm-active");
      let parentOfParent = currentParent.parentElement;
      if (parentOfParent && parentOfParent.classList) {
        parentOfParent.classList.add("mm-show");

        let grandParent = parentOfParent.parentElement;
        if (grandParent && grandParent.classList) {
          grandParent.classList.add("mm-active");
          if (grandParent.childNodes[0] && grandParent.childNodes[0].classList) {
            grandParent.childNodes[0].classList.add("mm-active");
          }

          let greatGrandParent = grandParent.parentElement;
          if (greatGrandParent && greatGrandParent.classList) {
            greatGrandParent.classList.add("mm-show");
            let greatGreatGrandParent = greatGrandParent.parentElement;
            if (greatGreatGrandParent && greatGreatGrandParent.classList) {
              greatGreatGrandParent.classList.add("mm-show");
              if (greatGreatGrandParent.childNodes[0] && greatGreatGrandParent.childNodes[0].classList) {
                greatGreatGrandParent.childNodes[0].classList.add("mm-active");
              }
            }
          }
        }
      }
    }
    scrollElement(item);
  }, []);

  const removeActivation = (items) => {
    for (let i = 0; i < items.length; ++i) {
      const item = items[i];
      if (!item || !item.classList) continue;

      if (item.classList.contains("active")) {
        item.classList.remove("active");
      }

      let currentParent = item.parentElement;
      if (currentParent && currentParent.classList) {
        const parent2El = (currentParent.childNodes && currentParent.childNodes.length > 1)
          ? currentParent.childNodes[1]
          : null;

        if (parent2El && parent2El.id !== "side-menu" && parent2El.classList) {
          parent2El.classList.remove("mm-show");
        }

        currentParent.classList.remove("mm-active");
        let parentOfParent = currentParent.parentElement;
        if (parentOfParent && parentOfParent.classList) {
          parentOfParent.classList.remove("mm-show");

          let grandParent = parentOfParent.parentElement;
          if (grandParent && grandParent.classList) {
            grandParent.classList.remove("mm-active");
            if (grandParent.childNodes[0] && grandParent.childNodes[0].classList) {
              grandParent.childNodes[0].classList.remove("mm-active");
            }

            let greatGrandParent = grandParent.parentElement;
            if (greatGrandParent && greatGrandParent.classList) {
              greatGrandParent.classList.remove("mm-show");
              let greatGreatGrandParent = greatGrandParent.parentElement;
              if (greatGreatGrandParent && greatGreatGrandParent.classList) {
                greatGreatGrandParent.classList.remove("mm-show");
                if (greatGreatGrandParent.childNodes[0] && greatGreatGrandParent.childNodes[0].classList) {
                  greatGreatGrandParent.childNodes[0].classList.remove("mm-active");
                }
              }
            }
          }
        }
      }
    }
  };

  const activeMenu = useCallback(() => {
    const ul = document.getElementById("side-menu");
    if (!ul) {
      return;
    }

    const items = ul.getElementsByTagName("a");
    removeActivation(Array.from(items));

    let matchingMenuItem = null;
    for (let i = 0; i < items.length; ++i) {
      if (items[i] && items[i].pathname && pathname === items[i].pathname) {
        matchingMenuItem = items[i];
        break;
      }
    }

    if (matchingMenuItem) {
      activateParentDropdown(matchingMenuItem);
    }
  }, [pathname, activateParentDropdown]);

  // Effect untuk inisialisasi MetisMenu, dipicu saat komponen di-mount atau userLevel berubah
  useEffect(() => {
    if (ref.current) {
      // Hapus instance MetisMenu yang sudah ada sebelum inisialisasi baru
      if (metisMenuInstanceRef.current) {
        metisMenuInstanceRef.current.dispose();
      }

      const menu = new MetisMenu("#side-menu");
      metisMenuInstanceRef.current = menu;
    }

    return () => {
      if (metisMenuInstanceRef.current) {
        metisMenuInstanceRef.current.dispose();
        metisMenuInstanceRef.current = null;
      }
    };
  }, [userLevel]); // <--- **Tambahkan userLevel di sini**

  // Effect untuk mengaktifkan menu setiap kali pathname berubah
  useEffect(() => {
    activeMenu();
  }, [pathname, activeMenu]);

  // Efek untuk recalculate SimpleBar, jika diperlukan
  useEffect(() => {
    if (ref.current) {
      ref.current.recalculate();
    }
  }, [pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  function scrollElement(item) {
    if (item && ref.current && ref.current.getScrollElement()) {
      const currentPosition = item.offsetTop;
      if (currentPosition > ref.current.getScrollElement().clientHeight || currentPosition < ref.current.getScrollElement().scrollTop) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref} style={{ backgroundColor: '#ffffff' }}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu">
            <b>
<li className="menu-title" style={{ color: TEXT_HIGHLIGHT_ORANGE, fontSize: '12px' }}> 
    {props.t("Pusat Langganan")}
</li>
            </b>
            <li>
              <Link to="/#" className="has-arrow text-body">
                <i className="bx bx-box text-body"></i>
                <span>{props.t("Paket Langganan")}</span>
              </Link>
              <ul className="sub-menu">
                <li><Link to="/langganan-bimbel?kategori_paket=bimbel&id_tipe_konten=2" className="text-body">{props.t("Bimbel")}</Link></li>
                <li><Link to="/langganan-tryout?kategori_paket=tryout&id_tipe_konten=2" className="text-body">{props.t("Tryout")}</Link></li>
              </ul>
            </li>

            <li className="menu-title" style={{ color: TEXT_HIGHLIGHT_ORANGE, fontSize: '12px' }}> 
              {props.t("Menu")}
            </li>
            <li>
              <Link to="/dashboard" className="text-body">
                <i className="bx bx-home-circle text-body"></i>
                <span>{props.t("Beranda")}</span>
              </Link>
            </li>
            
            {/* Rendering Kondisional untuk menu "Master" */}
            {userLevel === "super-admin" && (
              <li>
                <Link to="/#" className="has-arrow text-body">
                  <i className="bx bx-archive text-body"></i>
                  <span>{props.t("Master")}</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link to="/master-kategori" className="text-body">{props.t("Kategori Paket")}</Link>
                  </li>
                  <li>
                    <Link to="/master-tipe-konten" className="text-body">{props.t("Tipe Konten")}</Link>
                  </li>
                  <li>
                    <Link to="/master-jenis-seleksi" className="text-body">{props.t("Jenis Seleksi")}</Link>
                  </li>
                  <li>
                    <Link to="/master-jenis-soal" className="text-body">{props.t("Jenis Soal")}</Link>
                  </li>
                  <li>
                    <Link to="/master-paket" className="text-body">{props.t("Paket")}</Link>
                  </li>
                </ul>
              </li>
            )}
            
            {/* Rendering Kondisional untuk menu "Materi Belajar" */}
            {userLevel === "super-admin" && (
              <li>
                <Link to="/#" className="has-arrow text-body">
                  <i className="bx bx-folder text-body"></i>
                  <span>{props.t("Materi Belajar")}</span>
                </Link>
                <ul className="sub-menu">
                  <li>
                    <Link to="/soal-list" className="text-body">{props.t("List Soal")}</Link>
                  </li>
                   <li>
                    <Link to="/video-list" className="text-body">{props.t("List Video")}</Link>
                  </li>
                  <li>
                    <Link to="/bacaan-list" className="text-body">{props.t("List Bacaan")}</Link>
                  </li>
                </ul>
              </li>
            )}

          
            {userLevel === "super-admin" && (
                <li>
                  <Link to="/#" className="has-arrow text-body">
                    <i className="bx bx-cart text-body"></i>
                    <span>{props.t("Produk")}</span>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/list-produk" className="text-body">{props.t("List Produk")}</Link>
                    </li>
                  </ul>
                </li>
            )}

              {userLevel === "super-admin" && (
                <li>
                  <Link to="/#" className="has-arrow text-body">
                    <i className="bx bx-laptop text-body"></i>
                    <span>{props.t("Kelas Online")}</span>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/kelas-online" className="text-body">{props.t("List Kelas Online")}</Link>
                    </li>
                  </ul>
                </li>
            )}
            
            <React.Fragment> {/* Tambahkan elemen induk di sini */}
                <li>
                  <Link to="/#" className="has-arrow text-body">
                    <i className="bx bx-run text-body"></i>
                    <span>{props.t("Tryout")}</span>
                  </Link>
                  <ul className="sub-menu">
                    <li>
                      <Link to="/paket-tryout?kategori_paket=tryout&id_tipe_konten=2" className="text-body">{props.t("Tryout")}</Link>
                    </li>
                    <li>
                      <Link to="/paket-simulasi?kategori_paket=simulasi&id_tipe_konten=3" className="text-body">{props.t("Simulasi")}</Link>
                    </li>
                    <li>
                      <Link to="/paket-pembahasan?kategori_paket=pembahasan&id_tipe_konten=4" className="text-body">{props.t("Pembahasan")}</Link>
                    </li>
                  </ul>
                </li>
            </React.Fragment> 
            

            <li>
              <Link to="/#" className="has-arrow text-body">
                <i className="bx bxs-graduation text-body"></i>
                <span>{props.t("Belajar")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/paket-belajar?kategori_paket=latihan&id_tipe_konten=6" className="text-body">
                    {props.t("Latihan")}
                  </Link>
                </li>
                <li>
                  <Link to="/paket-kuis?kategori_paket=kuis&id_tipe_konten=7" className="text-body">
                    {props.t("Kuis")}
                  </Link>
                </li>
              </ul>
            </li>
             <li>
              <Link to="/#" className="has-arrow text-body">
                <i className="bx bxs-video text-body"></i>
                <span>{props.t("Video Belajar")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/paket-video?kategori_paket=belajar&id_tipe_konten=8" className="text-body">
                    {props.t("Video Belajar")}
                  </Link>
                </li>
                <li>
                  <Link to="/paket-rekaman?kategori_paket=rekaman&id_tipe_konten=8" className="text-body">
                    {props.t("Rekaman Kelas")}
                  </Link>
                </li>
              </ul>
            </li>
          <li>
            <Link to="/kelas-online-user" className="text-body">
              <i className="bx bx-chalkboard text-body"></i>
              <span>{props.t("Kelas Online")}</span>
            </Link>
          </li>

            <li>
              <Link to="/materi-bacaan?kategori_paket=bacaan&id_tipe_konten=5" className="text-body">
                <i className="bx bx-book-open text-body"></i>
                <span>{props.t("Materi Bacaan")}</span>
              </Link>
            </li>
            <li>
              <Link to="/raport-nasional?kategori_paket=raport&id_tipe_konten=2" className="text-body">
                <i className="bx bx-receipt text-body"></i>
                <span>{props.t("Raport Nasional")}</span>
              </Link>
            </li>
            <li className="menu-title" style={{ color: TEXT_HIGHLIGHT_ORANGE, fontSize: '12px' }}> 
              {props.t("Administrasi")}
            </li>
            <li>
              <Link to="/#" className="has-arrow text-body">
                <i className="bx bx-cog text-body"></i> 
                <span>{props.t("Pengaturan")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/pengaturan-profil" className="text-body">{props.t("Profil Saya")}</Link>
                </li>
              </ul>
            </li>
<li>
              <Link to="/#" className="has-arrow text-body">
                <i className="bx bx-credit-card-alt text-body"></i>
                <span>{props.t("Transaksi")}</span>
              </Link>
              <ul className="sub-menu">
                <li>
                  <Link to="/payments" className="text-body">{props.t("Riwayat Transaksi")}</Link>
                </li>
              </ul>
            </li>
            {/* Pengecekan Kondisi untuk "Manajemen Pengguna" */}
        {userLevel === "super-admin" && (
  <>
    <li>
      <a href="/#" className="has-arrow text-body">
        <i className="bx bx-group text-body"></i>
        <span>Manajemen Pengguna</span>
      </a>
      <ul className="sub-menu">
        <li>
          <a href="/user" className="text-body">Daftar Pengguna</a>
        </li>
      </ul>
    </li>
  </>
)}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(SidebarContent));